// src/pages/Manager/StageTrack.jsx
// STATUS: active | UPDATED: 2026-09-27
// LOG:
//   2026-09-27  New. Tyler: not a leaderboard, but completions of the sales
//               floor people, where they're at in the stages.
//
// WHERE EVERYBODY IS IN THE THIRTEEN, AND WHO MOVED.
//
// A track, not a table. Each face sits on the first step that person has not
// passed yet. That is a position, and position on its own reads as a ranking
// the moment two faces sit on different steps, so the one thing coloured on it
// is MOVEMENT: a green tick on anybody who passed a step in the last seven
// days. Somebody on Step 2 who moved on Thursday is doing better than somebody
// on Step 9 who has not moved in a fortnight, and this is how the screen says
// so without saying anybody is behind.
//
// NO NUMBERS BESIDE PEOPLE. No "7 of 13", no percentage. The step under the
// face is the fact; a percentage is a score.
//
// It scrolls sideways inside its own box on a phone, because thirteen columns
// will not fit 375px with a face in each and a wrapped track is not a track.

import { Box, Flex, HStack, Text, Tooltip, Icon } from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { Check } from 'lucide-react'
import { initialsFor, displayNameFor } from '../../lib/names'
import { stageFor, movedWithin } from '../../lib/marksMath'

const COL = 44

function Face({ person, moved }) {
  return (
    <Tooltip label={`${displayNameFor(person)}${moved ? ', moved this week' : ''}`} openDelay={250} hasArrow>
      <Box
        as={RouterLink}
        to={`/team/${person.id}/`}
        position="relative"
        display="block"
        lineHeight={0}
        borderRadius="full"
        _focusVisible={{ outline: '2px solid', outlineColor: 'accent.500', outlineOffset: '2px' }}
        aria-label={`${displayNameFor(person)}${moved ? ', moved this week' : ''}`}
      >
        <Flex
          boxSize="30px"
          borderRadius="full"
          bg={moved ? 'ink' : 'surface2'}
          color={moved ? 'onInk' : 'inkMuted'}
          border="1px solid"
          borderColor={moved ? 'ink' : 'lineStrong'}
          align="center"
          justify="center"
          fontSize="11px"
          fontWeight={600}
          transition="transform 140ms ease"
          _hover={{ transform: 'translateY(-2px)' }}
        >
          {initialsFor(person)}
        </Flex>
        {moved && (
          <Flex
            position="absolute"
            right="-3px"
            top="-3px"
            boxSize="14px"
            borderRadius="full"
            bg="success"
            color="onSuccess"
            align="center"
            justify="center"
            border="2px solid"
            borderColor="card"
          >
            <Icon as={Check} boxSize="8px" strokeWidth={4} />
          </Flex>
        )}
      </Box>
    </Tooltip>
  )
}

export default function StageTrack({ people, progress, now = new Date() }) {
  if (!people || people.length === 0) return null
  // Progress that failed to read is null. Drawing everybody on Step 1 would
  // be the swallowed-error bug in its most visual form, so the track says it
  // could not read rather than showing a floor that has never started.
  if (progress == null) {
    return (
      <Text fontSize="sm" color="inkDim">
        Could not read where everybody is right now.
      </Text>
    )
  }

  const placed = people.map((p) => ({
    person: p,
    at: stageFor(p.id, progress),
    moved: movedWithin(p.id, progress, now),
  }))
  const movedCount = placed.filter((x) => x.moved).length
  const columns = Array.from({ length: 13 }, (_, i) => i + 1)
  const finished = placed.filter((x) => x.at === 14)

  return (
    <Box>
      <HStack justify="space-between" align="baseline" mb={1} flexWrap="wrap" gap={2}>
        <Text fontSize="lg" fontWeight={600}>
          Where everybody is
        </Text>
        <Text fontSize="sm" fontFamily="mono" color={movedCount ? 'success' : 'inkDim'}>
          {movedCount === 0
            ? 'nobody moved this week'
            : `${movedCount} moved this week`}
        </Text>
      </HStack>
      <Text fontSize="body-sm" color="inkMuted" mb={4} maxW="measure" lineHeight={1.5}>
        Each face sits on the step they are working on. A position, not a race: the tick is
        anybody who passed a step in the last seven days.
      </Text>

      <Box
        overflowX="auto"
        pb={2}
        mx={-1}
        px={1}
        sx={{ '&::-webkit-scrollbar': { height: '6px' }, '&::-webkit-scrollbar-thumb': { background: 'var(--chakra-colors-line)', borderRadius: '3px' } }}
      >
        <Box minW={`${COL * 13 + (finished.length ? COL + 8 : 0)}px`}>
          <Flex align="flex-end" gap={0}>
            {columns.map((n) => {
              const here = placed.filter((x) => x.at === n)
              return (
                <Flex key={n} w={`${COL}px`} direction="column" align="center" gap={1.5} pb={2} minH="44px" justify="flex-end">
                  {here.map((x) => (
                    <Face key={x.person.id} person={x.person} moved={x.moved} />
                  ))}
                </Flex>
              )
            })}
            {finished.length > 0 && (
              <Flex w={`${COL}px`} ml={2} direction="column" align="center" gap={1.5} pb={2} justify="flex-end">
                {finished.map((x) => (
                  <Face key={x.person.id} person={x.person} moved={x.moved} />
                ))}
              </Flex>
            )}
          </Flex>

          {/* The rail. A solid dot where somebody is working, a hollow one
              where nobody is, so a floor bunched on Steps 2 and 3 is visible
              at a glance as the place the next morning meeting should go. */}
          <Flex position="relative" align="center" h="22px">
            <Box position="absolute" left={`${COL / 2}px`} right={`${COL / 2 + (finished.length ? COL + 8 : 0)}px`} h="2px" bg="line" />
            {columns.map((n) => {
              const occupied = placed.some((x) => x.at === n)
              return (
                <Flex key={n} w={`${COL}px`} justify="center" position="relative">
                  <Box
                    boxSize={occupied ? '10px' : '6px'}
                    borderRadius="full"
                    bg={occupied ? 'ink' : 'card'}
                    border="2px solid"
                    borderColor={occupied ? 'ink' : 'lineStrong'}
                  />
                </Flex>
              )
            })}
            {finished.length > 0 && (
              <Flex w={`${COL}px`} ml={2} justify="center">
                <Flex boxSize="16px" borderRadius="full" bg="success" color="onSuccess" align="center" justify="center">
                  <Icon as={Check} boxSize="10px" strokeWidth={3.5} />
                </Flex>
              </Flex>
            )}
          </Flex>
          <Flex>
            {columns.map((n) => (
              <Text key={n} w={`${COL}px`} textAlign="center" fontSize="xs" fontFamily="mono" color="inkDim">
                {n}
              </Text>
            ))}
            {finished.length > 0 && (
              <Text w={`${COL}px`} ml={2} textAlign="center" fontSize="xs" fontFamily="mono" color="success">
                done
              </Text>
            )}
          </Flex>
        </Box>
      </Box>
    </Box>
  )
}
